import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const blank = { name: "", location: "", is_active: true };

export default function Branches() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = create
    const [form, setForm] = useState(blank);

    const load = async () => {
        const { data } = await api.get("/admin/branches");
        setItems(data);
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
    const openEdit = (b) => {
        setEditing(b);
        setForm({ name: b.name, location: b.location || "", is_active: b.is_active });
        setOpen(true);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editing) {
                await api.patch(`/admin/branches/${editing.id}`, form);
                toast.success("Branch updated");
            } else {
                await api.post("/admin/branches", form);
                toast.success("Branch added");
            }
            setOpen(false);
            setEditing(null);
            setForm(blank);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const onToggle = async (b) => {
        await api.patch(`/admin/branches/${b.id}`, { name: b.name, location: b.location, is_active: !b.is_active });
        load();
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete branch?")) return;
        await api.delete(`/admin/branches/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div className="p-8 max-w-5xl" data-testid="branches-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="text-xs tracking-widest text-[#7A726D] uppercase">Network</div>
                    <h1 className="font-display text-4xl font-bold text-espresso tracking-tight">Branches</h1>
                </div>
                <Button onClick={openCreate} className="bg-espresso hover:bg-[#2c1a17] rounded-sm" data-testid="new-branch-button">
                    <Plus className="w-4 h-4 mr-1"/> Add branch
                </Button>
                <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(blank); } }}>
                    <DialogContent className="rounded-sm">
                        <DialogHeader>
                            <DialogTitle className="font-display text-espresso">{editing ? "Edit branch" : "New branch"}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={onSubmit} className="space-y-3" data-testid="branch-form">
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Name</Label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-sm mt-1" data-testid="branch-name-input"/>
                            </div>
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Location</Label>
                                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-sm mt-1" data-testid="branch-location-input"/>
                            </div>
                            <div className="flex items-center justify-between bg-[#F9F8F6] p-3 rounded-sm">
                                <div>
                                    <div className="text-sm font-medium text-espresso">Active</div>
                                    <div className="text-xs text-[#7A726D]">Inactive branches won't appear in the scanner.</div>
                                </div>
                                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="branch-active-switch"/>
                            </div>
                            <Button type="submit" className="bg-espresso w-full rounded-sm" data-testid="branch-submit-button">
                                {editing ? "Save changes" : "Create"}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <Card className="border-[#E5E0D8] shadow-none rounded-sm">
                <CardContent className="p-0">
                    <Table data-testid="branches-table">
                        <TableHeader>
                            <TableRow className="bg-[#F9F8F6]">
                                <TableHead>Name</TableHead><TableHead>Location</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-[#7A726D] py-8">No branches yet</TableCell></TableRow>}
                            {items.map((b) => (
                                <TableRow key={b.id}>
                                    <TableCell className="font-medium">{b.name}</TableCell>
                                    <TableCell className="text-[#7A726D]">{b.location || "—"}</TableCell>
                                    <TableCell>
                                        <Switch checked={b.is_active} onCheckedChange={() => onToggle(b)} data-testid={`branch-toggle-${b.id}`}/>
                                    </TableCell>
                                    <TableCell className="text-right space-x-2">
                                        <button onClick={() => openEdit(b)} className="text-[#C48464] hover:text-espresso inline-flex items-center" data-testid={`branch-edit-${b.id}`}>
                                            <Pencil className="w-4 h-4"/>
                                        </button>
                                        <button onClick={() => onDelete(b.id)} className="text-[#B24A45] inline-flex items-center" data-testid={`branch-delete-${b.id}`}>
                                            <Trash2 className="w-4 h-4"/>
                                        </button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
